import { toast } from '@raystack/apsara';

import api from '@src/api';
import { getForestBiomassOutputTilerUrl } from '@src/components/Custom/ModelCommonComponents/ForestBiosmassCommon';
import {
  setActiveInsight,
  setDate,
  setLeftColorScale,
  setLeftDate,
  setLeftUrl,
  setRightColorScale,
  setRightDate,
  setRightUrl,
  setVectorDataLoader
} from '@src/redux/_reducers/projectReducer';
import store from '@src/redux/store';

import {
  CHANGE_DETECTION_BLOCK_NAME,
  CHANGE_DETECTION_VIEW_MODES,
  CROP_PHEN_BLOCK_NAME,
  CROP_STRESS_BLOCK_NAME,
  CROP_TYPE_BLOCK_NAME,
  CROP_TYPE_HSI_BLOCK_NAME,
  FARM_BOUNDARY_BLOCK_NAME,
  FOREST_BIOMASS_BLOCK_NAME,
  GAP_FILLED_NDVI,
  LULC_BLOCK_NAME,
  PROSAIL_BLOCK_NAME,
  TREE_SPECIES_BLOCK_NAME,
  WQI_BLOCK_NAME
} from './constants/MODE_INFERENCE_DEFAULTS';
import { INPUT_FORMATS } from './constants/WORKFLOW_CONSTANTS';
import { getUSTimeFormat } from './dateUtils';
import { getCogURLForOutput } from './workFlowUtils';

export const getInsightPayload = ({ insight, format }) => {
  switch (insight.metadata?.block_name) {
    case LULC_BLOCK_NAME: {
      let lulc_raster_output =
        insight.output.find(
          output => output.format === INPUT_FORMATS.FormatRaster
        ) || {};
      // preview URL specifically needed for insights in split compare
      let previewUrl = getCogURLForOutput(lulc_raster_output, format);
      return {
        ...insight,
        preview_url: previewUrl
      };
    }

    case CROP_STRESS_BLOCK_NAME: {
      const crop_stress_output =
        insight.output.find(
          output => output.format === INPUT_FORMATS.FormatRaster
        ) || {};
      let previewUrl = getCogURLForOutput(crop_stress_output, format);
      return {
        ...insight,
        preview_url: previewUrl
      };
    }

    case FARM_BOUNDARY_BLOCK_NAME:
      return {
        ...insight,
        geojson: {},
        showLabels: true,
        hoverID: ''
      };

    case PROSAIL_BLOCK_NAME:
      return {
        ...insight,
        activeRaster: {}
      };

    case CROP_PHEN_BLOCK_NAME:
      return {
        ...insight,
        geojson: {},
        showLabels: false,
        highlightedFarms: [],
        seasonPoint: '',
        activeFarm: {},
        hoverID: ''
      };

    case CHANGE_DETECTION_BLOCK_NAME:
      return {
        ...insight,
        viewMode: CHANGE_DETECTION_VIEW_MODES.deltaRaster,
        asset_comp_opacity: [1],
        asset_ref_opacity: [1],
        childClasses: {},
        imageLayers: []
      };

    case WQI_BLOCK_NAME:
      return {
        ...insight,
        activeRaster: {}
      };

    case FOREST_BIOMASS_BLOCK_NAME: {
      let previewUrl = getForestBiomassOutputTilerUrl(insight.output, format);
      return {
        ...insight,
        preview_url: previewUrl
      };
    }

    case CROP_TYPE_BLOCK_NAME:
      return { ...insight, geojson: {}, showLabels: true, activeFarm: {} };

    case GAP_FILLED_NDVI:
      return { ...insight, geojson: {}, activeFarm: {} };

    case TREE_SPECIES_BLOCK_NAME:
      return { ...insight };

    case CROP_TYPE_HSI_BLOCK_NAME:
      return { ...insight };

    default:
      return {};
  }
};

export const setActiveInsightHandler = (
  insight,
  setStage,
  setActiveInsightComp,
  side
) => {
  const splitMode = store.getState().projects.visualize.splitMode;
  const activeAoi = store.getState().projects.activeAoi;
  const format = store.getState().sourceActions.tileRenderFormat;

  const obj = getInsightPayload({ insight, format });

  if (splitMode) {
    // split mode operation
    setActiveInsightComp(obj);

    // for left side
    if (side === 'left') {
      store.dispatch(setLeftDate(getUSTimeFormat(insight.metadata.date)));
      store.dispatch(setLeftColorScale(''));
      store.dispatch(setLeftUrl(obj.preview_url));
    }

    // for left side
    if (side === 'right') {
      store.dispatch(setRightDate(getUSTimeFormat(insight.metadata.date)));
      store.dispatch(setRightColorScale(''));
      store.dispatch(setRightUrl(obj.preview_url));
    }

    setStage('output');
    return;
  }

  store.dispatch(setActiveInsight(obj));
  store.dispatch(
    setDate(
      insight.metadata.date !== ''
        ? getUSTimeFormat(insight.metadata.date)
        : getUSTimeFormat(Object.keys(activeAoi.images)[0])
    )
  );
  setStage('output');
};

/**
 *
 * @param {Object} block - project.insights.activeModel from redux
 * @param {*} activeAoi - project.activeAoi from redux
 * @returns {Object} - payload create inference endpoint and pricing estimate endpoint
 */
export const InferecePayloadGenerator = (block, activeAoi, name) => {
  let dateTorun = '';

  const inputs = block.spec.inputs;
  // this is considering that not model will have both `raster` and `multiband` as inputs
  const raster_input = inputs.find(
    i => i.format === INPUT_FORMATS.FormatRaster
  );

  // some models will not have raster related input
  // for models that have, we populate dateToRun
  if (raster_input) {
    // locking this as only way to get input/output date metadata
    dateTorun = raster_input?.properties?.date ?? '';
  }

  // prepare inputs for model
  const payload = {
    block: {
      id: block.id,
      version: block.spec.version,
      name: block.spec.name
    },
    project_id: activeAoi.project_id,
    aoi_id: activeAoi.id,
    input: [],
    metadata: {
      block_name: block.spec.name,
      block_title: block.spec.title,
      description: block.spec.description,
      aoi_id: activeAoi.id,
      // for forest biomass
      date: inputs?.find(d => d.name === 'date_yr')?.value || dateTorun
    },
    name
  };

  // added extra metadata for change detection
  if (payload.metadata.block_name === CHANGE_DETECTION_BLOCK_NAME) {
    payload.metadata['date_comp'] = block.spec.insight_metadata.date_comp;
    payload.metadata['date_ref'] = block.spec.insight_metadata.date_ref;
    payload.metadata['asset_type'] = block.spec.insight_metadata.asset_type;
  }

  if (payload.metadata.block_name === CROP_TYPE_BLOCK_NAME) {
    payload.metadata['asset_timerange_start'] =
      block.spec.insight_metadata.asset_timerange_start;
    payload.metadata['asset_timerange_end'] =
      block.spec.insight_metadata.asset_timerange_end;
  }

  if (payload.metadata.block_name === GAP_FILLED_NDVI) {
    payload.metadata['asset_date'] = block.spec.insight_metadata.asset_date;
  }
  payload.input = [...block.spec.inputs];

  return payload;
};

export async function GetInferenceEstimate(setLoader, payload, setEstimate) {
  setLoader(true);
  try {
    const result = await api.dexter.post(
      `/v1alpha1/inferences/cost/estimate`,
      payload
    );
    setEstimate(result.data?.data?.estimate ?? 0);
  } catch (error) {
    toast.error('Failed to get Insight estimate!', {
      description:
        'There seems to be an issue from our end to estimate cost for this job. Please try again!'
    });
  } finally {
    setLoader(false);
  }
}

export async function fetchInsightVectorData(output, setVectorData) {
  const activeAoi = store.getState().projects.activeAoi;

  store.dispatch(setVectorDataLoader(true));
  try {
    const outputVector = await api.dataStore.post(
      `/${activeAoi.id}/assets/download`,
      {
        file_path: output.value.split('/').slice(3).join('/').split('?')[0],
        file_name: output.value
          .split('/')
          .slice(3)
          .join('/')
          .split('?')[0]
          .split('/')
          .at(-1)
      }
    );
    setVectorData(outputVector.data);
  } catch (error) {
    console.error(error);
    toast.error('Unable to fetch job data!', {
      description:
        'There seems to be an issue fetching job result, please try again!'
    });
  } finally {
    store.dispatch(setVectorDataLoader(false));
  }
}

export const getInputRasterPath = (inputs = []) => {
  const raster = inputs.find(i => i.format === INPUT_FORMATS.FormatRaster);
  return raster?.stac_url || raster?.value || '';
};
